"use client";

import { Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Inquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "What is the DDS qualification process?",
    answer: "Waters Well Inc. is a state-qualified provider. We work directly with your DDS case manager to ensure all documentation, eligibility requirements, and service authorizations are processed efficiently to begin your support journey."
  }, {
    question: "How do I request specific services?",
    answer: "You can initiate a service request through our secure online portal. Once submitted, our administrative team will review your needs and coordinate with your support team to schedule an initial consultation."
  }, {
    question: "Are your services available statewide?",
    answer: "We currently provide support services across Connecticut. Our team is dedicated to maintaining high standards of care regardless of your specific location within the state, ensuring consistent, reliable assistance."
  }, {
    question: "How do you ensure quality of care?",
    answer: "Quality is at the heart of our mission. We adhere strictly to Connecticut DDS regulations, conduct regular staff training, and perform ongoing internal audits to ensure our support remains person-centered and effective."
  }, {
    question: "Can I change my support plan?",
    answer: "Absolutely. We understand that individual needs evolve. We work closely with you and your support team to adjust your service plan, ensuring it always reflects your current goals and preferences."
  }, {
    question: "What is your staff training policy?",
    answer: "All staff members undergo rigorous background checks and comprehensive training programs mandated by the state, supplemented by our own internal curriculum focused on empathy, safety, and professional excellence."
  }, {
    question: "How do you handle emergency situations?",
    answer: "We maintain 24/7 communication protocols and clear emergency response procedures. Our staff is trained to act decisively and compassionately, ensuring the safety and well-being of every individual we serve."
  }, {
    question: "Is my personal information secure?",
    answer: "We prioritize data privacy and HIPAA compliance. All personal and medical information is stored in secure, encrypted systems, accessible only to authorized personnel involved in your direct care."
  }];
  return <section data-section-id="995" id="common-questions" className="relative overflow-hidden py-24 md:py-32 bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute -bottom-10 -right-10 hidden md:block" style={{
      opacity: 0.04
    }} aria-hidden="true">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727603/img/waters_well_emblem.png" alt="" className="h-[460px] w-[460px] object-contain" loading="lazy" decoding="async" />
      </div>

      <div ref={ref} className={`relative mx-auto max-w-4xl px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="flex justify-center">
          <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{
          color: "hsl(195 100% 40%)"
        }}>
            § 04 &mdash; Support Insights
          </span>
        </div>

        {}
        <h2 className="mt-8 text-center font-default font-light leading-[1.1] text-[44px] sm:text-[52px] md:text-[64px]">
          Common
          <br />
          Inquiries
          <span className="font-light tracking-[0.1em] text-muted-foreground">
            {" "}
            Answered.
          </span>
        </h2>

        {}
        <div className="mx-auto mt-16 max-w-[880px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border">
                <AccordionTrigger className="group py-7 hover:no-underline [&>svg]:hidden">
                  <span className="font-default font-medium text-xl md:text-[22px] leading-snug pr-4 text-left">
                    {faq.question}
                  </span>
                  <span className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center text-2xl font-light transition-transform duration-300 ease-out group-data-[state=open]:rotate-45" style={{
                color: "hsl(195 100% 40%)"
              }} aria-hidden="true">
                    +
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pr-12">
                  <p className="font-default text-base leading-[1.7] max-w-[720px] text-muted-foreground">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div className="mt-20 flex justify-center">
          <div className="w-full max-w-xl rounded-lg px-8 py-10 text-center border border-border" style={{
          backgroundColor: "hsl(215 16% 16%)"
        }}>
            <p className="font-default text-lg md:text-xl leading-relaxed">
              Need further assistance?
              <br />
              Our team is here to help.
            </p>
            <div className="mt-7">
              <Button asChild variant="outline" size="lg" className="font-default text-xs tracking-[0.2em] uppercase bg-transparent hover:bg-accent rounded-lg" style={{
              borderColor: "hsl(195 100% 40%)",
              color: "hsl(195 100% 40%)"
            }}>
                <Link to="/GetInTouchPage">
                  <Mail className="size-4 mr-2" />
                  Contact Support
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}